<template>
  <span
    :class="thumbnailClasses"
    :aria-busy="props.loading || undefined"
    data-cy="ds-thumbnail"
  >
    <span
      :class="DS_THUMBNAIL_CLASSES.Surface"
      data-cy="ds-thumbnail__surface"
    >
      <span
        v-if="props.loading"
        :class="DS_THUMBNAIL_CLASSES.Loading"
        data-cy="ds-thumbnail__loading"
        aria-hidden="true"
      >
        <DSIcon
          icon="reload"
          :size="contentIconSize"
          spin
        />
      </span>

      <img
        v-else-if="props.content === DS_THUMBNAIL_CONTENT.Image"
        :alt="props.alt ?? ''"
        :class="DS_THUMBNAIL_CLASSES.Image"
        :src="props.src"
        data-cy="ds-thumbnail__image"
      />

      <span
        v-else-if="props.content === DS_THUMBNAIL_CONTENT.Icon"
        :class="DS_THUMBNAIL_CLASSES.Icon"
        data-cy="ds-thumbnail__icon"
        aria-hidden="true"
      >
        <DSIcon
          :icon="resolvedIcon"
          :size="contentIconSize"
        />
      </span>

      <span
        v-else-if="props.content === DS_THUMBNAIL_CONTENT.Text && props.contentText"
        :class="DS_THUMBNAIL_CLASSES.Text"
        data-cy="ds-thumbnail__text"
      >
        {{ props.contentText }}
      </span>

      <span
        v-if="!props.loading && (props.overlayIcon || hasSlotContent(slots, 'actions'))"
        :class="DS_THUMBNAIL_CLASSES.Overlay"
        data-cy="ds-thumbnail__overlay"
        aria-hidden="true"
        inert
      >
        <DSIcon
          v-if="props.overlayIcon"
          :icon="props.overlayIcon"
          :size="overlayIconSize"
        />
      </span>

      <span
        v-if="hasSlotContent(slots, 'actions')"
        :class="DS_THUMBNAIL_CLASSES.Actions"
        data-cy="ds-thumbnail__actions"
      >
        <slot name="actions" />
      </span>

      <DSProgressBar
        v-if="props.loading && props.loadingProgress !== undefined"
        :class="DS_THUMBNAIL_CLASSES.Progress"
        :value="props.loadingProgress"
        data-cy="ds-thumbnail__progress"
      />
    </span>

    <span
      v-if="hasDescription"
      :class="DS_THUMBNAIL_CLASSES.Description"
      data-cy="ds-thumbnail__description"
    >
      <span
        v-if="props.fileName"
        :class="DS_THUMBNAIL_CLASSES.FileName"
        data-cy="ds-thumbnail__file-name"
      >
        <span
          :class="DS_THUMBNAIL_CLASSES.FileNameBase"
          data-cy="ds-thumbnail__file-name-base"
        >
          {{ fileNameParts.base }}
        </span>
        <span
          v-if="fileNameParts.extension"
          :class="DS_THUMBNAIL_CLASSES.FileNameExtension"
          data-cy="ds-thumbnail__file-name-extension"
        >
          {{ fileNameParts.extension }}
        </span>
      </span>
      <span
        v-if="props.fileSize || props.fileDate"
        :class="DS_THUMBNAIL_CLASSES.Metadata"
        data-cy="ds-thumbnail__metadata"
      >
        <span
          v-if="props.fileSize"
          :class="DS_THUMBNAIL_CLASSES.FileSize"
          data-cy="ds-thumbnail__file-size"
        >
          {{ props.fileSize }}
        </span>
        <span
          v-if="props.fileDate"
          :class="DS_THUMBNAIL_CLASSES.FileDate"
          data-cy="ds-thumbnail__file-date"
        >
          {{ props.fileDate }}
        </span>
      </span>
    </span>
  </span>
</template>

<script setup lang="ts">
import { computed, useSlots } from "vue";
import DSIcon from "@/src/design_system/primitives/icon/components/DSIcon.vue";
import DSProgressBar from "@/src/design_system/feedback/progress_bar/components/DSProgressBar.vue";
import {
  DS_THUMBNAIL_CLASSES,
  DS_THUMBNAIL_SIZE_TO_CLASS_MAP,
  DS_THUMBNAIL_SIZE_TO_CONTENT_ICON_SIZE_MAP,
  DS_THUMBNAIL_SIZE_TO_OVERLAY_ICON_SIZE_MAP,
} from "@/src/design_system/data_display/thumbnail/constants";
import {
  DS_THUMBNAIL_CONTENT,
  DS_THUMBNAIL_SIZES,
  type DSThumbnailProps,
  type DSThumbnailSize,
} from "@/src/design_system/data_display/thumbnail/types";
import { resolveThumbnailIcon } from "@/src/design_system/data_display/thumbnail/utils/resolveThumbnailIcon";
import { hasSlotContent } from "@/src/shared/utils/slots";

const props = withDefaults(defineProps<DSThumbnailProps>(), {
  size: DS_THUMBNAIL_SIZES.Medium,
  src: undefined,
  alt: undefined,
  fileName: undefined,
  contentText: undefined,
  overlayIcon: undefined,
  showDescription: false,
  fileSize: undefined,
  fileDate: undefined,
  loading: false,
  loadingProgress: undefined,
});

const slots = useSlots();

const normalizedSize = computed<DSThumbnailSize>(() => {
  return Object.values(DS_THUMBNAIL_SIZES).includes(props.size) ? props.size : DS_THUMBNAIL_SIZES.Medium;
});

const hasDescription = computed(() => {
  const supportsDescription =
    normalizedSize.value === DS_THUMBNAIL_SIZES.Large || normalizedSize.value === DS_THUMBNAIL_SIZES.ExtraLarge;
  const hasDescriptionData = [props.fileName, props.fileSize, props.fileDate].some(Boolean);

  return props.showDescription && supportsDescription && hasDescriptionData;
});

const thumbnailClasses = computed(() => [
  DS_THUMBNAIL_CLASSES.Root,
  DS_THUMBNAIL_SIZE_TO_CLASS_MAP[normalizedSize.value],
  { [DS_THUMBNAIL_CLASSES.WithDescription]: hasDescription.value },
]);

const contentIconSize = computed(() => DS_THUMBNAIL_SIZE_TO_CONTENT_ICON_SIZE_MAP[normalizedSize.value]);
const overlayIconSize = computed(() => DS_THUMBNAIL_SIZE_TO_OVERLAY_ICON_SIZE_MAP[normalizedSize.value]);
const resolvedIcon = computed(() =>
  resolveThumbnailIcon({
    fileName: props.fileName,
    size: normalizedSize.value,
  })
);

const fileNameParts = computed(() => {
  const fileName = props.fileName?.split(/[\\/]/).pop() ?? "";
  const separatorIndex = fileName.lastIndexOf(".");

  if (separatorIndex <= 0 || separatorIndex === fileName.length - 1) {
    return { base: fileName, extension: "" };
  }

  return {
    base: fileName.slice(0, separatorIndex),
    extension: fileName.slice(separatorIndex),
  };
});
</script>
